import { Injectable } from '@angular/core';
import sanitize from 'sanitize-filename';

import { AnyCustomPropertyValue } from '@reach/interfaces/custom-property/custom-property-value.interface';
import { FileUpdateBody } from '@reach/interfaces/files/files.interface';
import {
	CreateFilesResponse,
	ReplaceFileResponse,
	UpdateFileResponse,
} from '@reach/interfaces/files/files.responses';
import {
	NodeCreationSource,
	NodeCreationSourceBody,
} from '@reach/interfaces/nodes/node-creation-source.interface';
import { NodeInterface } from '@reach/interfaces/nodes/nodes.types';
import { CreateNoteResponse } from '@reach/interfaces/notes/notes.responses';

import { BaseApiService } from '~app-client/api/_internal';
import { ReachNodeId } from '~app-client/core/types';

@Injectable({ providedIn: 'root' })
export class FilesApiService extends BaseApiService {
	protected commonPath = ['files'];

	public async createFiles(
		files: File[],
		workspaceIds: ReachNodeId[],
		source: NodeCreationSource
	): Promise<CreateFilesResponse> {
		const body = new FormData();
		for (const file of files) {
			// Check if the file has a webkitRelativePath property and use it as the filename. We have to do it like this for the Safari browser.
			const filename = file.webkitRelativePath || file.name;
			body.append('files', file, filename);
		}

		const sourceItem: NodeCreationSourceBody = { source };

		body.append('source', JSON.stringify(sourceItem));
		body.append('fileCreate', JSON.stringify({}));
		body.append('workspaceIds', JSON.stringify(workspaceIds));

		return await this.http.post<CreateFilesResponse>(this.url(), body, {}, {}, undefined, {
			maxRetryAttempts: 3,
			scalingDuration: 1500,
			includedStatusCodes: [429, 502, 503, 504],
			backoff: 'exponential',
			jitter: true,
			maxDelayMs: 8000,
		});
	}

	public async edit(fileId: ReachNodeId, data: FileUpdateBody): Promise<UpdateFileResponse> {
		const body = data;
		return await this.http.patch<UpdateFileResponse>(this.url(fileId), body);
	}

	public async replace(fileId: NodeInterface['id'], file: File): Promise<ReplaceFileResponse> {
		const body = new FormData();
		body.append('file', file);

		return await this.http.post<ReplaceFileResponse>(this.url(fileId, 'replace'), body);
	}

	public async noteFromDocNode(nodeId: NodeInterface['id']): Promise<CreateNoteResponse> {
		return await this.http.post<CreateNoteResponse>(this.url(nodeId, 'docx-to-note'), {});
	}

	public async updateCustomProperties(
		fileId: ReachNodeId,
		customProperties: AnyCustomPropertyValue[],
		workspaceId: ReachNodeId
	): Promise<AnyCustomPropertyValue[]> {
		const body = {
			customProperties,
			fatherWorkspaceId: workspaceId,
		};
		return await this.http
			.patch<NodeInterface>(this.url(fileId), body)
			.then((node) => node?.customProperties || []);
	}
}

@Injectable({ providedIn: 'root' })
export class UserFilesApiService extends BaseApiService {
	protected commonPath = [];

	public async downloadFile(filePath: string): Promise<{ blob: Blob; extension: string | null }> {
		return await this.http.getBlobWithHeaders(filePath, {}, {}).then((response) => {
			const mimeType = response.headers.get('Content-Type') || '';
			const responseBody = response.body ?? new Uint8Array();
			const blob = new Blob([responseBody], { type: mimeType });
			return {
				blob,
				extension: getExtensionFromContentDisposition(
					response.headers.get('Content-Disposition') || ''
				),
			};
		});
	}

	public getFilename(name: string, extension): string {
		return sanitize(this.getFilenameWithoutSanitization(name, extension), {
			replacement: '_',
		}).replace(/[/\\?%*:|"<>; ]/g, '_');
	}

	private getFilenameWithoutSanitization(name: string, extension): string {
		if (name.endsWith(`.${extension}`)) {
			return name;
		}

		if (!extension) {
			return name;
		}

		return `${name}.${extension}`;
	}
}

function getExtensionFromContentDisposition(headerValue: string): string {
	const rawItemValue =
		headerValue
			.split(';')
			.find((part) => part.includes('filename='))
			?.trim()
			?.split('=')?.[1] || '';
	const itemValueWithSpaces = rawItemValue?.replaceAll('"', '') || '';

	const lastItemAux = itemValueWithSpaces.split(' ');
	const lastItem = lastItemAux[lastItemAux.length - 1];
	const extensionsAux = lastItem?.split('.') || [];
	return extensionsAux[extensionsAux.length - 1];
}
